import {
  PlaybookEvaluationType,
  TaskStatus,
  type ClauseSlim,
  type ClauseValidationOptionType,
  type ListPlaybookEvaluationsResponse,
  type PlaybookRiskLevel,
  type PlaybookRunInDb,
} from '@/api';
import {
  buildOptionMap,
  extractedValue,
  extractionResponseType,
  latestResultForEvaluation,
  parseSuggestionRedlines,
  resultSeverity,
  uncommonsFindings,
  type PlaybookActionResult,
  type PlaybookEvaluation,
  type PlaybookEvaluationOption,
  type PlaybookEvaluationResult,
  type SeverityBucket,
} from '@/services/playbooks/playbookRunDisplay';
import type {
  UncommonsCheckFinding,
  WordPromptError,
  WordRiskLevel,
  WordSuggestion,
} from '@/types/playbookWordAddin';
import { formatDisplayValue } from '@/utils/displayAiResponse';

/**
 * Maps a playbook run onto the view models the Word task pane already renders
 * (`WordSuggestion`, `UncommonsCheckFinding`) so the add-in's panes keep
 * their presentation while the data behind them comes from a playbook run.
 *
 * Playbook redlines carry no block anchor, so a suggestion is located by searching
 * the document for its `text_to_replace`.
 */

/** A clause-validation result, flattened for the Clause Validation pane. */
export interface PlaybookClauseEntry {
  id: string;
  evaluationName: string;
  optionType: ClauseValidationOptionType | null;
  optionName: string | null;
  riskLevel: PlaybookRiskLevel | null;
  reasoning: string | null;
  highlightedText: string[];
  clause: ClauseSlim | null;
  suggestions: WordSuggestion[];
}

export interface EvaluationProgress {
  done: number;
  total: number;
}

const SEVERITY_LEVELS: Record<SeverityBucket, WordRiskLevel> = {
  High: 'high',
  Medium: 'medium',
  Low: 'low',
  Other: 'unknown',
};

const UNFINISHED_STATUSES = new Set<TaskStatus>([TaskStatus.PENDING, TaskStatus.RUNNING]);

function evaluationsOfType(
  evaluations: ListPlaybookEvaluationsResponse,
  type: PlaybookEvaluationType
): PlaybookEvaluation[] {
  return evaluations.filter(evaluation => evaluation.evaluation_type === (type as string));
}

/** A result worth rendering: finished, and not a failure (those get their own alert). */
function isRenderable(result: PlaybookEvaluationResult | null): result is PlaybookEvaluationResult {
  return (
    result !== null &&
    !UNFINISHED_STATUSES.has(result.status) &&
    result.status !== TaskStatus.FAILED
  );
}

function matchedOptions(
  result: PlaybookEvaluationResult,
  optionMap: Map<string, PlaybookEvaluationOption>
): PlaybookEvaluationOption[] {
  return (result.matched_option_uids ?? [])
    .map(uid => optionMap.get(uid))
    .filter((option): option is PlaybookEvaluationOption => Boolean(option));
}

type SuggestionActionResult = Extract<PlaybookActionResult, { action_type: 'suggestion' }>;

function isLatestSuggestionActionResult(
  actionResult: PlaybookActionResult
): actionResult is SuggestionActionResult {
  return actionResult.action_type === 'suggestion' && actionResult.is_latest;
}

/** The latest suggestion action results of a result. */
function latestSuggestionActionResults(result: PlaybookEvaluationResult): SuggestionActionResult[] {
  return (result.action_results ?? []).filter(isLatestSuggestionActionResult);
}

/** The matched options' names as one label, or null when nothing matched. */
function joinOptionNames(options: PlaybookEvaluationOption[]): string | null {
  return options.map(option => option.name).join(', ') || null;
}

/** Redlines of a result, one `WordSuggestion` per replacement. */
function mapResultSuggestions(
  result: PlaybookEvaluationResult,
  evaluation: PlaybookEvaluation,
  options: PlaybookEvaluationOption[],
  severity: SeverityBucket
): WordSuggestion[] {
  const optionNames = joinOptionNames(options);

  return latestSuggestionActionResults(result).flatMap(actionResult =>
    parseSuggestionRedlines(actionResult.suggestion).map((redline, index) => ({
      id: `${actionResult.uid}:${index}`,
      result_type: 'suggestion' as const,
      title: evaluation.name,
      explanation: (redline.reason || result.reasoning) ?? '',
      status: 'pending' as const,
      text_to_replace: redline.string_to_replace,
      proposed_text: redline.replacement_string,
      extraction_highlighted_text: result.highlighted_text ?? null,
      source_prompt_uid: evaluation.uid,
      matched_option_names: optionNames,
      risk_assessment: { level: SEVERITY_LEVELS[severity], label: severity },
    }))
  );
}

/**
 * An informational card with no redline to apply. `result_type: 'extraction'` is
 * what the panes treat as "not actionable" — no Apply/Decline, just the value and
 * the reasoning — so it covers extraction results and matched classifications
 * whose option configured no suggestion action.
 */
function buildStaticCard(
  result: PlaybookEvaluationResult,
  evaluation: PlaybookEvaluation,
  answer: string | null,
  severity: SeverityBucket | null,
  matchedOptionNames: string | null = null
): WordSuggestion {
  return {
    id: result.uid,
    result_type: 'extraction',
    title: evaluation.name,
    explanation: result.reasoning ?? '',
    status: 'pending',
    extraction_answer: answer,
    matched_option_names: matchedOptionNames,
    extraction_highlighted_text: result.highlighted_text ?? null,
    source_prompt_uid: evaluation.uid,
    risk_assessment: severity ? { level: SEVERITY_LEVELS[severity], label: severity } : null,
  };
}

/** Classification results as suggestion cards for the Evaluations pane. */
export function mapRunToSuggestions(
  run: PlaybookRunInDb,
  evaluations: ListPlaybookEvaluationsResponse
): WordSuggestion[] {
  const optionMap = buildOptionMap(evaluations);

  return evaluationsOfType(evaluations, PlaybookEvaluationType.CLASSIFICATION).flatMap(
    evaluation => {
      const result = latestResultForEvaluation(run, evaluation.uid);
      if (!isRenderable(result)) {
        return [];
      }

      const options = matchedOptions(result, optionMap);
      const severity = resultSeverity(result, optionMap);
      const suggestions = mapResultSuggestions(result, evaluation, options, severity);
      if (suggestions.length > 0) {
        return suggestions;
      }

      // The matched class is the whole content of a classification card, so it goes in
      // the class pill rather than the card body.
      return [
        buildStaticCard(result, evaluation, null, severity, joinOptionNames(options) ?? 'No match'),
      ];
    }
  );
}

/** Extraction results as informational cards for the Extractions pane. */
export function mapRunToExtractions(
  run: PlaybookRunInDb,
  evaluations: ListPlaybookEvaluationsResponse
): WordSuggestion[] {
  return evaluationsOfType(evaluations, PlaybookEvaluationType.EXTRACTION).flatMap(evaluation => {
    const result = latestResultForEvaluation(run, evaluation.uid);
    if (!isRenderable(result)) {
      return [];
    }

    const responseType = extractionResponseType(evaluation) ?? undefined;
    const answer = formatDisplayValue(extractedValue(result), responseType);
    return [buildStaticCard(result, evaluation, answer, null)];
  });
}

/** Clause-validation results for the Clause Validation pane. */
export function mapRunToClauseEntries(
  run: PlaybookRunInDb,
  evaluations: ListPlaybookEvaluationsResponse
): PlaybookClauseEntry[] {
  const optionMap = buildOptionMap(evaluations);

  return evaluationsOfType(evaluations, PlaybookEvaluationType.CLAUSE_VALIDATION).flatMap(
    evaluation => {
      const result = latestResultForEvaluation(run, evaluation.uid);
      if (!isRenderable(result)) {
        return [];
      }

      const options = matchedOptions(result, optionMap);
      const severity = resultSeverity(result, optionMap);
      const option = options.at(0) ?? null;

      return [
        {
          id: result.uid,
          evaluationName: evaluation.name,
          optionType: option && 'option_type' in option ? (option.option_type ?? null) : null,
          optionName: option?.name ?? null,
          riskLevel: option?.risk_level ?? null,
          reasoning: result.reasoning ?? null,
          highlightedText: result.highlighted_text ?? [],
          clause: option && 'clause' in option ? (option.clause ?? null) : null,
          suggestions: mapResultSuggestions(result, evaluation, options, severity),
        },
      ];
    }
  );
}

/** Uncommons-check findings for the Uncommons pane. */
export function mapRunToFindings(
  run: PlaybookRunInDb,
  evaluations: ListPlaybookEvaluationsResponse
): UncommonsCheckFinding[] {
  return evaluationsOfType(evaluations, PlaybookEvaluationType.UNCOMMONS_CHECK).flatMap(
    evaluation => {
      const result = latestResultForEvaluation(run, evaluation.uid);
      if (!isRenderable(result)) {
        return [];
      }

      return uncommonsFindings(result).map((finding, index) => ({
        id: `${result.uid}:${index}`,
        text: finding.text,
        reason: finding.reason ?? '',
        severity: finding.severity ?? null,
        status: 'open' as const,
      }));
    }
  );
}

/** Failed evaluations of a type, for the panes' "failed" alert. */
export function mapFailedEvaluations(
  run: PlaybookRunInDb,
  evaluations: ListPlaybookEvaluationsResponse,
  type: PlaybookEvaluationType
): WordPromptError[] {
  return evaluationsOfType(evaluations, type).flatMap(evaluation => {
    const result = latestResultForEvaluation(run, evaluation.uid);
    if (!result || result.status !== TaskStatus.FAILED) {
      return [];
    }

    return [
      {
        id: result.uid,
        title: evaluation.name,
        message: result.error ?? 'The evaluation failed.',
      },
    ];
  });
}

/** How many evaluations of the given type already have a result in this run. */
export function countEvaluationProgress(
  run: PlaybookRunInDb | null,
  evaluations: ListPlaybookEvaluationsResponse,
  type: PlaybookEvaluationType
): EvaluationProgress {
  const relevant = evaluationsOfType(evaluations, type);
  if (!run) {
    return { done: 0, total: relevant.length };
  }

  const done = relevant.filter(evaluation => {
    const result = latestResultForEvaluation(run, evaluation.uid);
    return result !== null && !UNFINISHED_STATUSES.has(result.status);
  }).length;
  return { done, total: relevant.length };
}
